import React, { useState, useEffect, useRef, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useHistory } from 'react-router-dom'
import { ROUTE_PATHS } from 'constants/index'
import './MediaBanner.scss'

const HOT_MARKETS = [
  { q: 'Will Bitcoin reach $150K in 2025?', yes: 68, cat: 'Crypto' },
  { q: 'Will the Fed cut rates twice in Q3?',  yes: 41, cat: 'Economy' },
  { q: 'Will ETH outperform BTC this year?',  yes: 29, cat: 'Crypto' },
]

function PredictionSlide ({ history }) {
  return (
    <div className='mb-pm-slide'>
      <div className='mb-pm-slide__bg' />
      <div className='mb-pm-slide__inner'>
        <div className='mb-pm-slide__left'>
          <span className='mb-pm-slide__eyebrow'>Powered by Polymarket</span>
          <h2 className='mb-pm-slide__title'>
            Prediction<br /><span>Markets</span>
          </h2>
          <p className='mb-pm-slide__desc'>
            Trade YES / NO outcomes on real-world events.<br />
            On-chain liquidity. Instant settlement.
          </p>
          <button
            className='mb-pm-slide__btn'
            onClick={() => history.push(ROUTE_PATHS.PREDICTION_MARKET)}
          >
            Explore Markets
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='M5 12h14M12 5l7 7-7 7' /></svg>
          </button>
        </div>
        <div className='mb-pm-slide__right'>
          <div className='mb-pm-slide__panel-label'>
            <span className='mb-pm-slide__live-dot' />
            Live Markets
          </div>
          {HOT_MARKETS.map((m, i) => (
            <div key={i} className='mb-pm-slide__row'>
              <span className='mb-pm-slide__row-cat'>{m.cat}</span>
              <span className='mb-pm-slide__row-q'>{m.q}</span>
              <div className='mb-pm-slide__row-odds'>
                <span className='yes'>{m.yes}%</span>
                <span className='no'>{100 - m.yes}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const IMAGE_DURATION = 4500

const MediaBanner = () => {
  const history = useHistory()

  const SLIDES = [
    { src: '/assets/banners/fwc.jpeg',                        label: 'FWC',               accent: '#FFD700' },
    { src: '/assets/banners/Casino%20banner%202025.jpg',       label: 'Casino 2025',       accent: '#FFC84C' },
    { src: '/assets/banners/Sports%20onsite%201.jpg',          label: 'Sports Betting',    accent: '#72F238' },
    { src: '/assets/banners/casino%20banner%202025%201.jpg',   label: 'Casino Collection', accent: '#8FFFEE' },
    { type: 'custom', content: <PredictionSlide history={history} />, label: 'Predict', accent: '#FFC84C' },
  ]

  const [active, setActive]     = useState(0)
  const [prev, setPrev]         = useState(null)
  const [dir, setDir]           = useState(1)
  const [paused, setPaused]     = useState(false)
  const [loaded, setLoaded]     = useState({})
  const [progress, setProgress] = useState(0)

  const timerRef  = useRef(null)
  const progRef   = useRef(null)
  const startRef  = useRef(null)

  const goTo = useCallback((idx, direction) => {
    setDir(direction)
    setPrev(active)
    setActive(idx)
    setProgress(0)
  }, [active])

  const next = useCallback(() => goTo((active + 1) % SLIDES.length, 1),  [active, goTo, SLIDES.length])
  const back = useCallback(() => goTo((active - 1 + SLIDES.length) % SLIDES.length, -1), [active, goTo, SLIDES.length])

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current)
    clearInterval(progRef.current)
    startRef.current = Date.now()
    setProgress(0)
    progRef.current = setInterval(() => {
      setProgress(Math.min(((Date.now() - startRef.current) / IMAGE_DURATION) * 100, 100))
    }, 40)
    timerRef.current = setTimeout(next, IMAGE_DURATION)
  }, [active, next])

  useEffect(() => {
    if (paused) { clearTimeout(timerRef.current); clearInterval(progRef.current); return }
    startTimer()
    return () => { clearTimeout(timerRef.current); clearInterval(progRef.current) }
  }, [active, paused, startTimer])

  useEffect(() => {
    const handler = (e) => { if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') back() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [next, back])

  const accent = SLIDES[active].accent

  return (
    <div
      className='mb-carousel'
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role='region'
      aria-label='Promotions carousel'
    >
      <div className='mb-carousel__track'>
        {SLIDES.map((slide, i) => {
          const isActive = i === active
          const isPrev   = i === prev
          const slideDir = isActive ? dir : (isPrev ? -dir : 0)

          return (
            <div
              key={i}
              className={[
                'mb-carousel__slide',
                isActive ? 'mb-carousel__slide--active' : '',
                isPrev   ? 'mb-carousel__slide--exit'   : ''
              ].join(' ')}
              style={{ '--slide-dir': slideDir }}
              aria-hidden={!isActive}
            >
              {slide.type === 'custom'
                ? slide.content
                : (
                  <>
                    <img
                      className='mb-carousel__media'
                      src={slide.src}
                      alt={slide.label}
                      draggable={false}
                      onLoad={() => setLoaded(p => ({ ...p, [i]: true }))}
                    />
                    {!loaded[i] && <div className='mb-carousel__shimmer' />}
                    <div className='mb-carousel__overlay' />
                    <div className='mb-carousel__label' style={{ '--accent': slide.accent }}>
                      <span className='mb-carousel__label-dot' />
                      {slide.label}
                    </div>
                  </>
                )
              }
            </div>
          )
        })}
      </div>

      <div className='mb-carousel__progress'>
        <div className='mb-carousel__progress-fill' style={{ width: `${progress}%`, background: accent }} />
      </div>

      <button className='mb-carousel__arrow mb-carousel__arrow--left'  onClick={back} aria-label='Previous slide'><ChevronLeft  size={20} strokeWidth={2.5} /></button>
      <button className='mb-carousel__arrow mb-carousel__arrow--right' onClick={next} aria-label='Next slide'><ChevronRight size={20} strokeWidth={2.5} /></button>

      <div className='mb-carousel__dots' role='tablist'>
        {SLIDES.map((slide, i) => (
          <button
            key={i}
            className={`mb-carousel__dot ${i === active ? 'mb-carousel__dot--active' : ''}`}
            style={i === active ? { '--dot-accent': slide.accent } : {}}
            onClick={() => goTo(i, i > active ? 1 : -1)}
            role='tab'
            aria-selected={i === active}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export default React.memo(MediaBanner)
